/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 18-07-2026
 *
 * De resultaatweergave van een simulatie: leest de huidige selectie op het sim-canvas en
 * tekent de bijbehorende waarden en (on)gelijkheden als transiënte simnodes op de modelgraaf.
 * Geport uit `cytocanvas.simview.controller.js` (golf 5c van Stap 5).
 *
 * Geen Angular-service maar een gewone klasse: `DlSimCanvasComponent` bezit één instantie en
 * geeft die zijn afhankelijkheden (de `DlModel`-API, de sim-`cy` en de logger) via de constructor.
 * De klasse is bewust stateless — het `simulatie`-object (eigenaar: het component) komt per
 * aanroep mee, zodat er geen tweede bron van waarheid ontstaat.
 *
 * De value-history (`showHistory`, golf 5d) en ineq-history (`showIneqHistory`, golf 5e) wonen hier
 * ook; het component draait de bijbehorende multi-select-lus (`toonValuehistories`/`toonIneqhistories`).
 */
import type { CollectionReturnValue, Core, NodeCollection, NodeSingular } from 'cytoscape';
import type { Sihwlog } from 'sihw-ngx';
import type { DlModel } from '../../../app/model/dl-model';
import type { IneqHistoryEntry, SimulatieData } from './dl-simcanvas.component';
import { stateVanNode, transitieVanNode } from './sim-lookup';

/** Eén (on)gelijkheid in de simulatiedata: verwijst met `from`/`to` naar element-ids of 'zero'. */
export interface SimIneq {
    from: string;
    to: string;
    type: string;
}

/** Eén calculus-argument in de simulatiedata (bijv. de som van twee quantities). */
export interface SimCalc {
    from: string;
    to: string;
    type: string;
}

/**
 * De simulatiedata van één state of transitie zoals aangeleverd door het backend: per element de
 * waarde (`values`/`dvalues`), de (on)gelijkheden (`q_ineq`/`d_ineq`) en de calculi (`q_calcs`/
 * `d_calcs`).
 */
export interface SimResultaatData {
    values: Record<string, string>;
    dvalues: Record<string, string>;
    q_ineq: SimIneq[];
    d_ineq: SimIneq[];
    q_calcs: Record<string, SimCalc>;
    d_calcs: Record<string, SimCalc>;
    [key: string]: unknown;
}

/** Cytoscape-collectie uitgebreid met de projectextensies (zie `cytoscape-extensies.ts`). */
type MetElementExtensies = {
    outElements: (selector?: string) => NodeCollection;
    inElements: (selector?: string) => NodeCollection;
};

/** Eén quantity-space-element zoals opgeslagen in de node-data (voor de value-history-labels). */
interface QsItemData {
    type: string;
    id: string;
    label: string;
    isZero?: boolean;
}

/** De relevante simulatiedata van één state voor de value-history: nummer, waarden en afgeleiden. */
interface SimHistoryStateData {
    num: number;
    values: Record<string, string>;
    dvalues: Record<string, string>;
    d2values: Record<string, string>;
    d3values: Record<string, string>;
}

/** De simulatiedata van één state voor de ineq-history: het statenummer plus de (on)gelijkheden/calculi. */
interface SimIneqHistoryStateData extends SimResultaatData {
    num: number;
}

/** Vaste maatvoering (in pixels) voor het tekenen van een value-historyblok. */
const HISTORY_INSTELLINGEN = {
    afstand_x: 150, // pixels vanaf de qnode
    kolom: 30, // pixels voor elke statekolom
    mainvalue: 20, // pixels voor de waarde zelf binnen de kolom
    hogereOrdeKolom: 10, // pixels voor 2e en 3e afgeleide elk
    interval: 20, // pixels voor elke interval
    point: 20, // pixels voor elk point (grijze ruimte met een wit streepje)
    staterow: 20, // hoogte voor de staterow
    labelSpace: 15 // pixels tussen container en label
};

export class SimResultaatTekenaar {

    /** De query-/mutatie-API over de modelgraaf. */
    private readonly model: DlModel;

    /** De cytoscape-instantie van het sim-canvas (de stategraph). */
    private readonly simCy: Core;

    /** De logger van het bezittende component. */
    private readonly log: Sihwlog;

    constructor(model: DlModel, simCy: Core, log: Sihwlog) {
        this.model = model;
        this.simCy = simCy;
        this.log = log;
    }

    /**
     * Verwijder de resultaten van een simulatiestap uit het model. Bewaart eerst de posities van
     * elementen met een `savedposkey` in `simulatie.savedPos`, zodat ze over states heen consistent
     * blijven staan.
     */
    public clearResult(simulatie: SimulatieData | null): void {
        // opslaan pos van elementen met een savedposkey
        if (simulatie) {
            if (!simulatie.savedPos) {
                simulatie.savedPos = {};
            }
            this.model.simulationContent('[savedposkey]').forEach((n) => {
                simulatie.savedPos![n.data('savedposkey')] = { ...n.position() };
            });
        }
        this.model.removeSimulation();
        this.model.setInSimulate(false); // weer tonen
    }

    /**
     * Toon het resultaat van de huidige geselecteerde simulatie-elementen (states, transities) in
     * het model. Waarden tonen we alleen bij precies één selectie; de value-/ineq-histories (bij meer
     * selecties) tekent het component daarna (`toonValuehistories`/`toonIneqhistories`).
     * @param simulatie De actieve simulatie (eigenaar: het component)
     * @param forceRegenerate Als gegeven, dan worden niet de oude nodes van de state hergebruikt
     */
    public showResult(simulatie: SimulatieData | null, forceRegenerate?: boolean): void {
        // het kan voorkomen dat dit dubbel gebeurt (bijv. bij stateselect én daarna updatesimulatie);
        // dat nemen we voor lief. Checken of er wel een wijziging is werkt niet, want bij bijv.
        // een andere display van de histories moet het opnieuw, ondanks dezelfde stateselectie

        const geselecteerd = this.simCy.nodes(':selected') as unknown as CollectionReturnValue;
        this.clearResult(simulatie);

        // als niets geselecteerd, dan de begintoestand — al geregeld door clearResult
        if (geselecteerd.length === 0) {
            return;
        }

        // hoe dan ook, nu de te verbergen modelzaken verbergen
        this.model.setInSimulate(true);

        // de value-/ineq-history-lus bij multi-select draait het component (`showResult` van
        // `DlSimCanvasComponent`) na deze aanroep

        // waarden e.d. tonen we alleen bij 1 selectie
        if (geselecteerd.length !== 1) {
            return;
        }

        if (!stateVanNode(simulatie, geselecteerd)) {
            // beginstate (of een node die niet bij deze simulatie hoort), toon de modelelementen en klaar
            this.model.setInSimulate(false);
            return;
        }

        // hebben we hem al?
        const simnodes = geselecteerd.scratch('savednodes');
        if ((!forceRegenerate) && simnodes) {
            this.model.addSimnodes(simnodes);
            // reset de positie van elementen die bij een modelnode horen, en plaats de andere op een
            // eventuele nieuwe plek
            this.model.simulationContent().forEach((node) => {
                const kp = node.data('keep_position'); // bevat from: de modelnode en x/y als afwijking daarvan
                if (kp) {
                    const modelpos = kp.from.position();
                    node.scratch('prevPos', false); // voorkom automove van de rest
                    node.position({ x: modelpos.x + kp.x, y: modelpos.y + kp.y });
                } else {
                    this.checkSavedPos(simulatie, node); // kijk of er een opgeslagen positie is
                }
            });
            return;
        }

        // en tonen: stateinhoud en mogelijk terminatie-inhoud
        if (geselecteerd.data('type') === 'transitie') {
            // dan dus eerst de stateinhoud
            this.generateResultnodes(simulatie, stateVanNode(simulatie, geselecteerd)!, 'state');
            // en dan de transitie zelf
            this.generateResultnodes(simulatie, transitieVanNode(simulatie, geselecteerd)!,
                geselecteerd.data('transitietype'));
        } else {
            // gewoon een state
            this.generateResultnodes(simulatie, stateVanNode(simulatie, geselecteerd)!, 'state');
        }

        // opslaan in de scratch van de node
        geselecteerd.scratch('savednodes', this.model.simulationContent('.simcache'));
    }

    /**
     * Check of de node een key heeft voor een opgeslagen positie; zo ja en er staat een positie onder
     * die key, dan zetten we hem daar. Hierdoor krijgen verschillende nodes met dezelfde identiteit
     * (bijv. verschillende ineqs "tussen A en B") dezelfde plek over states heen.
     * @returns `true` als er een positie was en die is toegepast
     */
    private checkSavedPos(simulatie: SimulatieData | null, node: NodeSingular): boolean {
        const key = node.data('savedposkey');
        if (key && simulatie?.savedPos?.[key]) {
            node.position({ ...simulatie.savedPos[key] });
            return true;
        }
        return false;
    }

    /**
     * Genereer de simnodes voor de opgegeven simdata: quantity-waarden, afgeleide-waarden en
     * quantity-/derivative-(on)gelijkheden.
     * @param data simulatiedata uit de state of terminatie
     * @param simtype op te slaan attribuut bij de nodes ('state', 'terminatie', 'ordering')
     */
    private generateResultnodes(simulatie: SimulatieData | null, data: SimResultaatData, simtype: string): void {
        // values
        for (const [q, val] of Object.entries(data.values)) {
            let qsItem;
            if (val === 'no_qs_default_interval') {
                // by convention: een waarde op een door ons niet weergegeven quantityspace, die in de
                // simulatie standaard een intervalwaarde krijgt — skip
                continue;
            }
            if (val === 'zero') {
                qsItem = this.model.relatedZeroNode(this.model.getElement(q) as unknown as NodeSingular);
                if (!qsItem) {
                    continue; // skip
                }
            } else {
                // gewoon een nodeid
                qsItem = this.model.getElement(val);
                if (qsItem.empty()) {
                    continue; // skip
                }
            }
            const item = qsItem as unknown as NodeSingular;
            // toevoegen als simnode
            this.model.addSimnodes({
                classes: 'simulation quantity_value simcache',
                data: {
                    type: 'quantity_value',
                    simtype: simtype,
                    id: item.id() + '_simval',
                    automovegroup: item.data('automovegroup'), // alle elementen van deze qs bewegen samen
                    // speciaal in simulatie: info over de te bewaren positie
                    keep_position: {
                        from: item,
                        x: -30,
                        y: 0
                    }
                },
                position: {
                    x: item.position('x') - 30,
                    y: item.position('y')
                }
            });
        }

        // dvalues
        for (const [q, val] of Object.entries(data.dvalues)) {
            const qNode = this.model.getElement(q);
            if (qNode.empty()) {
                this.log.error('Kon quantity voor dvalue niet vinden', q, val);
                continue; // skip
            }
            const dqs = this.model.derivativeQs(qNode as unknown as NodeSingular);
            let dqe: NodeCollection | undefined;
            switch (val) {
                case 'plus':
                    dqe = dqs['derivative_plus'];
                    break;
                case 'zero':
                    dqe = dqs['derivative_zero'];
                    break;
                case 'min':
                    dqe = dqs['derivative_min'];
                    break;
            }
            if (!dqe) {
                this.log.error('Kon afgeleide waarde voor dvalue niet vinden', q, val);
                continue; // skip
            }
            const dNode = dqe as unknown as NodeSingular;
            // exogene d-value kan best wijzigen (zero worden als stijgen niet meer kan), dus we beelden
            // ze gewoon af
            this.model.addSimnodes({
                classes: 'simulation derivative_value simcache',
                data: {
                    type: 'derivative_value',
                    id: dNode.id() + '_simdval',
                    simtype: simtype,
                    automovegroup: dNode.data('automovegroup'), // alle elementen van deze qs bewegen samen
                    // speciaal in simulatie: info over de te bewaren positie
                    keep_position: {
                        from: dNode,
                        x: -30,
                        y: 0
                    }
                },
                position: {
                    x: dNode.position('x') - 30,
                    y: dNode.position('y')
                }
            });
        }

        // quantity ineq
        for (const ineq of data.q_ineq) {
            // Bert Bredeweg 7-2-2016: skip inequalities met calcs als argument — die laten we uit het model staan
            if (data.q_calcs[ineq.from] || data.q_calcs[ineq.to]) {
                continue;
            }
            if (this.isTussenTweeNulpunten(ineq)) {
                continue;
            }
            const from = this.getIneqArg('q', ineq.from, ineq.to, data, simtype);
            const to = this.getIneqArg('q', ineq.to, ineq.from, data, simtype);
            // als 1 van de 2 false is, kan de relatie niet gemaakt worden — die skippen we
            if (!(from && to)) {
                this.log.error('q_ineq te weinig info', ineq.from, ineq.to);
                continue;
            }
            this.addSimRelation(simulatie, from, to, ineq.type, 'ineq', simtype);
        }

        // derivative ineq
        for (const ineq of data.d_ineq) {
            // Bert Bredeweg 7-2-2016: skip inequalities met calcs als argument — die laten we uit het model staan
            if (data.d_calcs[ineq.from] || data.d_calcs[ineq.to]) {
                continue;
            }
            if (this.isTussenTweeNulpunten(ineq)) {
                continue;
            }
            const from = this.getIneqArg('d', ineq.from, ineq.to, data, simtype);
            const to = this.getIneqArg('d', ineq.to, ineq.from, data, simtype);
            // idem: skip als een van beide false is
            if (!(from && to)) {
                this.log.error('d_ineq te weinig info', ineq.from, ineq.to);
                continue;
            }
            this.addSimRelation(simulatie, from, to, ineq.type, 'd_ineq', simtype);
        }
    }

    /**
     * Of een (on)gelijkheid uit de simulatie tussen twee nulpunten loopt. Zo'n relatie bestaat
     * inhoudelijk niet: kwalitatief simuleren werkt zonder eenheden, dus in Garp is elke nul
     * dezelfde nul (0 = 0, absoluut) — de simulator geeft elk nulpunt, van een quantity space of van
     * een afgeleide, dan ook als het kale argument `zero` terug (`q_arg` in `helpers/simulator.js`).
     * Er valt niets te tekenen en de modelrelatie blijft in de simulatie verborgen.
     *
     * Hoort eigenlijk al bij het bouwen tegengehouden te worden (besluit gebruiker 07-09-2026):
     * tussen twee afgeleide-nulpunten altijd, tussen twee nulpunten van quantity spaces zolang ze
     * beide nul zijn — en de simulator slaat hem over. Deze vangnet blijft nodig voor bestaande
     * modellen en voor een qs-punt dat ná het leggen van de relatie op nul gezet wordt. Tot golf a2
     * stap 2 (bevinding test 8) logde dit een warn plus een error.
     */
    private isTussenTweeNulpunten(ineq: SimIneq): boolean {
        if (ineq.from !== 'zero' || ineq.to !== 'zero') {
            return false;
        }
        this.log.debug('(on)gelijkheid tussen twee nulpunten: geen simversie te tekenen', ineq);
        return true;
    }

    /**
     * Vind de juiste node voor een (on)gelijkheids-argument in de simulator: bij een calc-argument
     * `getCalc`, bij 'zero' een passende zeronode, anders de node zelf (quantity) of de derivative
     * eronder.
     * @returns een node of `false`
     */
    private getIneqArg(qOrD: 'q' | 'd', arg: string, otherarg: string, state: SimResultaatData, _simtype: string): NodeSingular | false {
        const calcs = qOrD === 'q' ? state.q_calcs : state.d_calcs;
        let res: CollectionReturnValue | NodeCollection | NodeSingular | false;
        // als het een calc is kijken we of die calc er al is, of maken hem ad hoc
        if (calcs[arg]) {
            res = this.getCalc(state, qOrD, arg);
        }
        // bij zero moeten we een zero vinden in de qs van het andere argument
        else if (arg === 'zero') {
            res = this.zeroNodeForArgument(qOrD, state, otherarg);
        }
        // anders is dit een node, quantity of point in ineq
        else {
            const node = this.model.getElement(arg);
            if (qOrD === 'q') {
                res = node;
            } else {
                // node is gegarandeerd een quantity, we zoeken de derivative eronder
                res = (node as unknown as MetElementExtensies).outElements('[type = "derivative"]');
            }
        }
        return (res && res.nonempty()) ? (res as unknown as NodeSingular) : false;
    }

    /**
     * Vind de node die de gegeven calc representeert. Volgens Bert komt dit in de praktijk niet voor,
     * behalve in de ineq-history.
     * @returns de node of `false` als het niet lukt
     */
    private getCalc(state: SimResultaatData, qOrD: 'q' | 'd', arg: string): NodeCollection | false {
        this.log.debug('getCalc aangeroepen voor een simulatiecalculus - die zou niet bestaan');
        const calcs = qOrD === 'q' ? state.q_calcs : state.d_calcs;
        const structuur = calcs[arg];
        this.log.debug('structuur', structuur);
        let calc: NodeCollection | false = false;
        if (structuur) {
            this.log.debug('links', this.model.getElement(structuur.from)?.data('type'), (this.model.getQuantity(structuur.from) || undefined)?.data('label'));
            this.log.debug('rechts', this.model.getElement(structuur.to)?.data('type'), (this.model.getQuantity(structuur.to) || undefined)?.data('label'));
            calc = this.model.getCalcFromTo(`${qOrD}_calc_${structuur.type}`, structuur.from, structuur.to);
        }
        this.log.debug('calc', calc);
        return calc;
    }

    /**
     * Geeft een handige zeronode terug gegeven een niet-zero argument van een (on)gelijkheid (d of q):
     * recurseert bij een calc-argument, zoekt anders de quantity omhoog en pakt de zero in zijn qs (of
     * de `derivative_zero`).
     * @returns de zeronode, of `false` als die niet gevonden kan worden
     */
    private zeroNodeForArgument(qOrD: 'q' | 'd', state: SimResultaatData, ineqArg: string): NodeSingular | NodeCollection | false {
        if (ineqArg === 'zero') {
            // we kunnen geen node vinden als dit arg zelf ook zero is
            this.log.warn('zeroNodeForArgument bij dubbele zero', qOrD, state, ineqArg);
            return false;
        }
        // is het een calc?
        const calcs = qOrD === 'q' ? state.q_calcs : state.d_calcs;
        if (calcs[ineqArg]) {
            // zoek in de from of de to, we willen een zero vinden
            return this.zeroNodeForArgument(qOrD, state, calcs[ineqArg].from) ||
                this.zeroNodeForArgument(qOrD, state, calcs[ineqArg].to);
        }
        // dan is het een node: we zoeken omhoog naar de quantity en dan in zijn qs naar zero
        const qnode = this.model.getQuantity(ineqArg);
        if (!qnode) {
            return false; // niet gevonden
        }
        let zero: NodeSingular | NodeCollection | undefined;
        if (qOrD === 'q') {
            const related = this.model.relatedZeroNode(qnode);
            zero = related || undefined;
        } else {
            zero = this.model.derivativeQs(qnode)['derivative_zero'];
        }
        return zero ? zero : false;
    }

    /**
     * Maak een simulatie-relatie (ineq/calc) tussen twee nodes, à la addRelation maar voor de
     * simulatie. Bepaalt de plek op basis van een opgeslagen positie, een gelijksoortige model-ineq of
     * een verzonnen plek met overlap-check.
     * @param savedposkey key voor het opslaan van de positie over states
     * @param simtype type simnode waarvoor we tekenen (state, terminatie, ordering)
     * @returns de nieuwe node
     */
    private addSimRelation(simulatie: SimulatieData | null, startNode: NodeSingular, endNode: NodeSingular, relationType: string, savedposkey: string, simtype: string): CollectionReturnValue {
        // bepaal eerst de savedposkey: relaties van hetzelfde soort (maar andere waarde) komen zo op
        // dezelfde plek over states heen — een > en later een < tussen twee Q's is 'dezelfde'
        const keyarg1 = startNode.data('savedposkey') || startNode.id();
        const keyarg2 = endNode.data('savedposkey') || endNode.id();

        savedposkey += '_';
        if (keyarg1 < keyarg2) { // sorteren
            savedposkey += keyarg1 + '_' + keyarg2;
        } else {
            savedposkey += keyarg2 + '_' + keyarg1;
        }

        // De simulator kan dezelfde ongelijkheid twee keer in één toestand of terminatie leveren.
        // Gezien 09-09-2026 in het model 'migratie a4 normmodel': de terminatie onder toestand 2
        // draagt `d_ineq_lte` van een afgeleide naar zijn nulpunt twee keer. De id is per toestand
        // uniek, dus de tweede aanmaak liet cytoscape gooien ("Can not create second element with
        // ID") en daarmee strandde het hele tekenen van die selectie. Eén keer tekenen is precies
        // goed: het is dezelfde relatie tussen dezelfde twee argumenten.
        const id = `${savedposkey}_${relationType}`;
        const bestaand = this.model.getElement(id);
        if (bestaand.nonempty()) {
            return bestaand;
        }

        // bepaal de plaatsingspositie
        let pos = null;
        if (simulatie?.savedPos?.[savedposkey]) {
            pos = { ...simulatie.savedPos[savedposkey] };
        } else {
            // is er een inequality tussen dezelfde argumenten in het model?
            const modelineq = this.model.getIneqBetween(startNode, endNode);
            if (modelineq) {
                pos = { ...modelineq.position() };
            }
        }
        // anders maar gewoon een plek verzinnen
        if (!pos) {
            const startNodepos = startNode.position();
            const endNodepos = endNode.position();

            // altijd er precies tussenin en iets lager dan het midden
            pos = { x: 0, y: startNodepos.y + (endNodepos.y - startNodepos.y) / 2 };
            if (Math.abs(endNodepos.x - startNodepos.x) < 10) {
                // zelfde positie
                pos.x = endNodepos.x - 100;
            } else {
                pos.x = startNodepos.x + (endNodepos.x - startNodepos.x) / 2; // ertussen
                pos.y += 100; // iets naar onderen
            }

            // kijk of de nodes die er al zijn dit raken
            for (let poging = 0; poging < 10; poging++) {
                if (this.model.overlap(pos, 50)) {
                    pos.y += 20; // iets lager dan?
                } else {
                    break; // gevonden
                }
            }
        }

        const n = this.model.addSimnodes({
            classes: 'simcache simulation ' + relationType,
            data: {
                type: relationType,
                id, // type toegevoegd om uniek te maken bij meerdere ineqs
                savedposkey: savedposkey,
                // en de from en to zoals bij gewone ineqs
                from: startNode.id(),
                to: endNode.id(),
                simtype: simtype
            },
            position: pos
        });
        this.model.addEdgeFromTo(startNode, n as unknown as NodeSingular, 'source', 'simulation simcache').data('simtype', simtype);
        this.model.addEdgeFromTo(endNode, n as unknown as NodeSingular, 'target', 'simulation simcache').data('simtype', simtype);
        this.checkSavedPos(simulatie, n as unknown as NodeSingular); // weten we al waar hij moet?
        return n;
    }

    /**
     * Toon de value-history voor één quantity: tekent per geselecteerde state een kolom met de waarde
     * (plus eventuele hogere-orde-afgeleiden) in een blok naast de quantity op de modelgraaf. De
     * container krijgt een `simhistoryVerberg`-scratch die de (ng21) prullenbak aanroept om deze
     * history te verbergen (de verbergroute van het simcanvas).
     * @param simulatie De actieve simulatie (eigenaar: het component); de statewaarden komen hieruit
     * @param qNode de quantitynode waarvoor we de history tekenen
     * @param states de state-nodes op volgorde (padvolgorde of selectievolgorde)
     * @param forceReposition herplaats het blok, negeer een eerder opgeslagen positie
     * @param verbergCb wordt via de container-scratch door de prullenbak aangeroepen
     */
    public showHistory(
        simulatie: SimulatieData | null,
        qNode: NodeSingular,
        states: NodeSingular[],
        forceReposition: boolean | undefined,
        verbergCb: (container: NodeSingular) => void
    ): void {
        const s = HISTORY_INSTELLINGEN;
        if (states.length < 2) {
            return;
        }
        const qid = qNode.id();
        const amg = 'history' + qid; // automovegroup-id

        // hoeveel hogere-orde-afgeleiden zijn zichtbaar? (1 = 2e afgeleide, 2 = 3e afgeleide)
        let numhogereorde = 0;
        for (const state of states) {
            const sim = stateVanNode(simulatie, state) as unknown as SimHistoryStateData;
            if (!sim) continue;
            // 3e afgeleide wordt zichtbaar als hij ergens een andere waarde heeft dan nil
            if (sim.d3values[qid] && sim.d3values[qid] !== 'nil') {
                numhogereorde = 2;
                break; // hoger wordt het niet
            } else if (sim.d2values[qid]) {
                numhogereorde = 1; // kan nog hoger worden
            }
        }
        const hogereOrdeRuimte = numhogereorde * s.hogereOrdeKolom;

        // de qs-waarden op volgorde van boven naar beneden
        const qs: QsItemData[] = [];
        let numpoints = 0;
        let numints = 0;
        const qsNode = this.model.getQSNode(qNode);
        if (qsNode) {
            let qsEl: NodeSingular | null = qsNode as unknown as NodeSingular;
            while ((qsEl = this.model.nextQsItem(qsEl) as unknown as NodeSingular | null)) {
                const qsdata = { ...qsEl.data() } as QsItemData;
                if (qsdata.type === 'quantity_space_point') {
                    numpoints++;
                } else {
                    numints++;
                }
                qs.push(qsdata);
            }
        } else {
            numints = 1;
            qs.push({
                type: 'quantity_space_interval',
                id: 'no_qs_default_interval', // conventie: dit geeft de backendsimulator terug
                label: ''
            });
        }

        const infosize = { // het infovlak
            w: states.length * (s.kolom + hogereOrdeRuimte),
            h: numints * s.interval + numpoints * s.point
        };
        const boxsize = { // de hele box
            w: infosize.w,
            h: infosize.h + s.staterow
        };

        // positie: we werken niet met savedpos vanwege de variabele grootte (pos = center)
        let oldpos = qNode.scratch('historylefttop') as { left: number; top: number } | undefined;
        if (forceReposition || !oldpos) {
            oldpos = {
                left: qNode.position('x') + s.afstand_x,
                top: qNode.position('y')
            };
        }
        const box = {
            left: oldpos.left,
            top: oldpos.top,
            right: oldpos.left + boxsize.w,
            bottom: oldpos.top + boxsize.h
        };

        // de container (géén compound: die zou achter andere nodes verdwijnen)
        const container = this.model.addSimnodes({
            group: 'nodes',
            classes: 'simulation simhistory_container',
            position: {
                x: box.left + boxsize.w / 2,
                y: box.top + boxsize.h / 2
            },
            data: {
                type: 'simhistory_container',
                id: 'histcontainer_' + qid, // vast id, zodat leiders en volgers hetzelfde houden
                historyquantity: qid,
                automovegroup: amg,
                height: boxsize.h + 'px', // via een css-truukje
                width: boxsize.w + 'px'
            }
        }) as unknown as NodeSingular;
        // de verbergroute voor de prullenbak als scratch, opgehaald door DlModelCanvasComponent
        container.scratch('simhistoryVerberg', () => verbergCb(container));
        // bewaar steeds de topleft voor herpositioneren
        container.on('position', () => {
            qNode.scratch('historylefttop', {
                left: container.position('x') - boxsize.w / 2,
                top: container.position('y') - boxsize.h / 2
            });
        });

        // subnode-tap selecteert de container, zodat de (selectie-gestuurde) prullenbak verschijnt.
        // We ruimen eerst de bestaande selectie op: `selectionType: 'single'` geldt alleen voor
        // native tap-selectie, niet voor een programmatische `select()` op een (niet-selecteerbare)
        // subnode — zonder deze opruiming stapelen selecties (meerdere histories, of node + history).
        const tapContainer = (): void => {
            container.cy().$(':selected').unselect();
            container.select();
        };

        this.model.addEdgeFromTo(qNode, container, 'normal', 'simulation');

        // een grijs vlak als achtergrond
        this.model.addSimnodes({
            group: 'nodes',
            selectable: false,
            classes: 'simulation simhistory simhistory_background',
            position: {
                x: box.left + boxsize.w / 2, // in het midden van de box
                y: box.top + infosize.h / 2 // niet helemaal in het midden, vanwege de staterow
            },
            data: {
                id: container.id() + '_bg',
                historyquantity: qid,
                automovegroup: amg,
                height: infosize.h + 'px',
                width: infosize.w + 'px'
            }
        }).on('tap', tapContainer);

        // de labels rechts erachter
        const labelX = box.right + s.labelSpace;
        let y = box.top; // helemaal boven
        const valueY: Record<string, number> = {};
        let i = 1;
        for (const qsEl of qs) {
            const yruimte = qsEl.type === 'quantity_space_point' ? s.point : s.interval;
            valueY[qsEl.id] = y + yruimte / 2; // midden in de beschikbare ruimte
            if (qsEl.isZero) {
                valueY['zero'] = valueY[qsEl.id]; // dat is dan de waardenaam in values
            }
            this.model.addSimnodes({
                group: 'nodes',
                selectable: false,
                classes: 'simulation simhistory simhistory_valuelabel',
                position: {
                    x: labelX,
                    y: valueY[qsEl.id]
                },
                data: {
                    label: qsEl.isZero ? 'Ø' : qsEl.label,
                    id: container.id() + '_' + i,
                    isZero: qsEl.isZero,
                    historyquantity: qid,
                    automovegroup: amg
                }
            }).on('tap', tapContainer);
            if (qsEl.type === 'quantity_space_point') {
                // een achtergrondnode
                this.model.addSimnodes({
                    group: 'nodes',
                    selectable: false,
                    classes: 'simulation simhistory simhistory_pointblock',
                    position: {
                        x: box.left + boxsize.w / 2,
                        y: y + s.point / 2
                    },
                    data: {
                        id: container.id() + '_' + i + '_bg',
                        historyquantity: qid,
                        automovegroup: amg,
                        width: boxsize.w + 'px' // hoogte via de style
                    }
                }).on('tap', tapContainer);
            }
            y += yruimte;
            i++;
        }

        // statenummers en values
        states.forEach((sn, nr) => {
            const sim = stateVanNode(simulatie, sn) as unknown as SimHistoryStateData;
            if (!sim) return;
            const x = box.left + nr * (s.kolom + hogereOrdeRuimte) + 0.5 * s.kolom; // midden in het valuemarker-deel
            this.model.addSimnodes({
                group: 'nodes',
                selectable: false,
                classes: 'simulation simhistory simhistory_statenumber',
                position: {
                    x: x,
                    y: box.bottom - s.staterow / 2 // midden in de staterow
                },
                data: {
                    automovegroup: amg,
                    id: container.id() + '_statenr_' + nr,
                    historyquantity: qid,
                    num: sim.num,
                    height: s.staterow + 'px',
                    width: s.kolom + 'px'
                }
            }).on('tap', tapContainer);
            // en de value
            if (sim.values[qid]) {
                this.model.addSimnodes({
                    group: 'nodes',
                    selectable: false,
                    classes: 'simulation simhistory simhistory_valuemarker simhistory_valuemarker_' + (sim.dvalues[qid] || 'unknown'),
                    position: {
                        x: x,
                        y: valueY[sim.values[qid]]
                    },
                    data: {
                        type: 'simhistory_valuemarker',
                        id: container.id() + '_statemarker_' + nr,
                        automovegroup: amg,
                        historyquantity: qid,
                        dir: sim.dvalues[qid] || '?'
                    }
                }).on('tap', tapContainer);
                // 2e afgeleide, skip unknown en nil
                if (numhogereorde > 0 && sim.d2values[qid] && sim.d2values[qid] !== 'unknown' && sim.d2values[qid] !== 'nil') {
                    this.model.addSimnodes({
                        group: 'nodes',
                        selectable: false,
                        classes: 'simulation simhistory simhistory_dvaluemarker simhistory_dvaluemarker_' + sim.d2values[qid],
                        position: {
                            x: x + s.mainvalue / 2 + s.hogereOrdeKolom / 2, // meteen ertegenaan
                            y: valueY[sim.values[qid]]
                        },
                        data: {
                            type: 'simhistory_d2valuemarker',
                            id: container.id() + '_statemarker_2e_' + nr,
                            automovegroup: amg,
                            historyquantity: qid,
                            dir: sim.d2values[qid] || '?'
                        }
                    }).on('tap', tapContainer);
                }
                // 3e afgeleide, skip unknown en nil
                if (numhogereorde > 1 && sim.d3values[qid] && sim.d3values[qid] !== 'unknown' && sim.d3values[qid] !== 'nil') {
                    this.model.addSimnodes({
                        group: 'nodes',
                        selectable: false,
                        classes: 'simulation simhistory simhistory_dvaluemarker simhistory_dvaluemarker_' + sim.d3values[qid],
                        position: {
                            x: x + s.mainvalue / 2 + 1.5 * s.hogereOrdeKolom,
                            y: valueY[sim.values[qid]]
                        },
                        data: {
                            type: 'simhistory_d2valuemarker',
                            id: container.id() + '_statemarker_3e_' + nr,
                            automovegroup: amg,
                            historyquantity: qid,
                            dir: sim.d3values[qid] || '?'
                        }
                    }).on('tap', tapContainer);
                }
            }
        });
    }

    /**
     * Toon de ineq-history voor één model-(on)gelijkheid: tekent per geselecteerde state een kolom met
     * het gevonden (on)gelijkheidstype als marker, in een blok onder de ineq op de modelgraaf. De
     * container krijgt een `simhistoryVerberg`-scratch die de (ng21) prullenbak aanroept om deze history
     * te verbergen (de verbergroute van het simcanvas).
     * @param simulatie De actieve simulatie (eigenaar: het component); de statewaarden komen hieruit
     * @param ineqkey de key (`from-to`) van de (on)gelijkheid; drijft de `historyineq`-selector
     * @param saved de entry uit `simulatie.ineqHistory` (eigenaar: het component); wordt in-place
     *   gemuteerd (`historylefttop`, `perState`)
     * @param states de state-nodes op volgorde (padvolgorde of selectievolgorde)
     * @param forceReposition herplaats het blok, negeer een eerder opgeslagen positie
     * @param verbergCb wordt via de container-scratch door de prullenbak aangeroepen
     */
    public showIneqHistory(
        simulatie: SimulatieData | null,
        ineqkey: string,
        saved: IneqHistoryEntry,
        states: NodeSingular[],
        forceReposition: boolean | undefined,
        verbergCb: (container: NodeSingular) => void
    ): void {
        const s = HISTORY_INSTELLINGEN;
        const numstates = states.length;
        if (numstates < 2) {
            return;
        }
        const amg = 'ineqhistory_' + ineqkey; // automovegroup-id

        const infosize = { // het infovlak
            w: numstates * s.kolom,
            h: s.point // gewoon 1 value
        };
        const boxsize = { // de hele box
            w: infosize.w,
            h: infosize.h + s.staterow
        };

        // positie staat in de ineqHistory-entry
        let oldpos = saved.historylefttop;
        if (forceReposition || !oldpos) {
            const ineq = this.model.getElement(saved.ineq) as unknown as NodeSingular;
            oldpos = saved.historylefttop = {
                left: ineq.position('x'),
                top: ineq.position('y') + ineq.height() / 2 + 20
            };
        }
        const box = {
            left: oldpos.left,
            top: oldpos.top,
            right: oldpos.left + boxsize.w,
            bottom: oldpos.top + boxsize.h
        };

        // de container (géén compound: die zou achter andere nodes verdwijnen)
        const container = this.model.addSimnodes({
            group: 'nodes',
            classes: 'simulation simhistory_container',
            position: {
                x: box.left + boxsize.w / 2,
                y: box.top + boxsize.h / 2
            },
            data: {
                type: 'simhistory_container',
                id: 'histcontainer_' + ineqkey, // vast id, zodat leiders en volgers hetzelfde houden
                historyineq: ineqkey,
                automovegroup: amg,
                height: boxsize.h + 'px', // via een css-truukje
                width: boxsize.w + 'px'
            }
        }) as unknown as NodeSingular;
        // de verbergroute voor de prullenbak als scratch, opgehaald door DlModelCanvasComponent
        container.scratch('simhistoryVerberg', () => verbergCb(container));
        // bewaar steeds de topleft voor herpositioneren
        container.on('position', () => {
            saved.historylefttop = {
                left: container.position('x') - boxsize.w / 2,
                top: container.position('y') - boxsize.h / 2
            };
        });

        // subnode-tap selecteert de container, zodat de (selectie-gestuurde) prullenbak verschijnt.
        // Eerst de bestaande selectie opruimen: `selectionType: 'single'` geldt alleen voor native tap,
        // niet voor een programmatische `select()` op een niet-selecteerbare subnode.
        const tapContainer = (): void => {
            container.cy().$(':selected').unselect();
            container.select();
        };

        this.model.addEdgeFromTo(this.model.getElement(saved.from) as unknown as NodeSingular, container, 'source', 'simulation');
        this.model.addEdgeFromTo(this.model.getElement(saved.to) as unknown as NodeSingular, container, 'target', 'simulation');

        // een grijs vlak als achtergrond
        this.model.addSimnodes({
            group: 'nodes',
            selectable: false,
            classes: 'simulation simhistory simhistory_background',
            position: {
                x: box.left + boxsize.w / 2, // in het midden van de box
                y: box.top + infosize.h / 2 // niet helemaal in het midden, vanwege de staterow
            },
            data: {
                id: container.id() + '_bg',
                historyineq: ineqkey,
                automovegroup: amg,
                height: infosize.h + 'px',
                width: infosize.w + 'px'
            }
        }).on('tap', tapContainer);

        // statenummers en values
        states.forEach((sn, nr) => {
            const sim = stateVanNode(simulatie, sn) as unknown as SimIneqHistoryStateData;
            if (!sim) return;
            const x = box.left + (nr + 0.5) * s.kolom; // midden in het blok
            this.model.addSimnodes({
                group: 'nodes',
                selectable: false,
                classes: 'simulation simhistory simhistory_statenumber',
                position: {
                    x: x,
                    y: box.bottom - s.staterow / 2 // midden in de staterow
                },
                data: {
                    automovegroup: amg,
                    id: container.id() + '_statenr_' + nr,
                    historyineq: ineqkey,
                    num: sim.num,
                    height: s.staterow + 'px',
                    width: s.kolom + 'px'
                }
            }).on('tap', tapContainer);

            // het ineq-type per state: uit de cache, of anders opzoeken via de from/to-argumenten
            let ineqtype: string | false | undefined = saved.perState[sim.num];
            if (!ineqtype) {
                const ineqs = saved.derivative ? sim.d_ineq : sim.q_ineq;
                const qd: 'q' | 'd' = saved.derivative ? 'd' : 'q';
                ineqtype = false;
                for (const ineq of ineqs) {
                    const fromarg = this.getIneqArg(qd, ineq.from, ineq.to, sim, 'state');
                    if (fromarg && fromarg.id() === saved.from) { // direct checken
                        const toarg = this.getIneqArg(qd, ineq.to, ineq.from, sim, 'state');
                        if (toarg && toarg.id() === saved.to) {
                            // deze ineq is goed
                            ineqtype = ineq.type;
                            break;
                        }
                    }
                }
            }
            const type = ineqtype || 'unknown'; // altijd tekenen, ook bij unknown
            saved.perState[sim.num] = type; // niet nog een keer uitzoeken
            this.model.addSimnodes({
                group: 'nodes',
                selectable: false,
                classes: 'simulation simhistory simhistory_ineqmarker simhistory_ineqmarker_' + type,
                position: {
                    x: x,
                    y: box.top + s.interval / 2
                },
                data: {
                    type: 'simhistory_ineqemarker',
                    id: container.id() + '_stateineqmarker_' + nr,
                    automovegroup: amg,
                    historyineq: ineqkey
                }
            }).on('tap', tapContainer);
        });
    }
}
